import React, {useState, useMemo, useEffect} from 'react';
import {
  Box,
  Flex,
  Divider,
  VStack,
  useTheme,
} from '@chakra-ui/react';
import {
  useGetUserMessages,
  useGetUserMessageStatus,
} from '../../hooks/useMessages';
import styled from 'styled-components';
import {TextG14, TextA18} from '../typography';
import IconCustom from '../../components/Icon';
import MessageCard from '../MessageCard';
import {UserMessage} from '../../types/message';
import {useNavigate} from 'react-router-dom';
import Modal from '../Modals/Modal';
import {SpinnerLoader} from '../Loader';

const TriggerContainer = styled(Flex)`
  height: 100%;
  min-width: 40px;
  justify-content: center;
  align-items: center;
`;

const IconContainer = styled(Box)`
  position: relative;
  cursor: pointer;
`;

const NotificationsLauncher = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [shouldFetchMessages, setShouldFetchMessages] = useState(false);
  const {data, isLoading} = useGetUserMessages(shouldFetchMessages);
  const { data: statusData } = useGetUserMessageStatus();
  
  // Derive unread status from status endpoint (single source of truth)
  const hasUnread = statusData?.data?.has_unread || false;
  
  // Derive preview messages from query data
  const messages = useMemo<UserMessage[]>(() => {
    return data?.pages[0]?.data?.data?.slice(0, 3) || [];
  }, [data]);

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      fontSize: '1.6rem',
      background: 'transparent',
      borderRadius: '100%',
      ...(show && {color: theme.colors.blue}),
    }),
    [theme, show]
  );

  useEffect(() => {
    setShow(false);
  }, [location.href]);

  const header = <TextA18>Notifications</TextA18>;
  const footer = (
    <TextG14
      w="100%"
      align="center"
      color={theme.colors.blue}
      onClick={() => {
        navigate('/notifications');
      }}>
      View All
    </TextG14>
  );

  const modalBody = useMemo(
    () =>
      isLoading ? (
        <SpinnerLoader height="100px" />
      ) : messages && messages.length ? (
        <>
          {messages.map((message: UserMessage) => {
            return (
              <VStack key={message.uuid} spacing={0} align="stretch">
                <MessageCard {...message} />
                <Divider
                  opacity="1"
                  borderColor={theme.colors.lighterGrey}
                />
              </VStack>
            );
          })}
        </>
      ) : (
        <>
          <TextG14 align="center" p={2} color={theme.colors.grey}>
            You have no unread Messages
          </TextG14>
          <Divider />
        </>
      ),
    [isLoading, messages]
  );

  return (
    <>
      <TriggerContainer>
        <IconContainer>
          {hasUnread && (
            <Box 
              position="absolute" 
              top="-1px" 
              right="-1px" 
              zIndex={1}
              role="status"
              aria-label="Unread messages indicator"
            >
              <Box
                borderRadius="full"
                backgroundColor={theme.colors.red}
                w="11px"
                h="11px"
              />
            </Box>
          )}
          <IconCustom
            onClick={() => {
              setShouldFetchMessages(true);
              setShow(!show);
            }}
            iconName={show ? 'fi-sr-bell' : 'fi-rr-bell'}
            style={iconStyle}
            aria-label={hasUnread ? 'Notifications - You have unread messages' : 'Notifications'}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShouldFetchMessages(true);
                setShow(!show);
              }
            }}
          />
        </IconContainer>
      </TriggerContainer>
      <Modal
        header={header}
        children={modalBody || <></>}
        footer={footer}
        show={show}
        parentCallback={() => {
          setShow(false);
        }}
        modalPadding={0}
        closeButton
        containerProps={{zIndex: 10000}}
      />
    </>
  );
};

export default NotificationsLauncher;
